import { createContext, ReactNode, useContext, useState } from "react";
import { Errors } from "Util/CustomTypes";
import { isEmail } from "Validators/Functions/Email";
import { FormFieldDefinition, SelectValueType } from "Views/Components/TemplateForm/TemplateFormTypes";
import { IMultiSelectQuantityValueType, MultiSelectQuantityValueType } from "../MultiSelectQuantity/MultiSelectQuantity";

/**
 * 
 * Context for handling schema and submission on schema and validation on schema
 */
export type TemplateFormValue = string | number | null | undefined | SelectValueType
	| MultiSelectQuantityValueType | IMultiSelectQuantityValueType;

export type TemplateForm = Record<string, TemplateFormValue>;

export type HandleInputChangeType = (field: FormFieldDefinition, value: any) => void;
export type HandleInputBlurType = (field: FormFieldDefinition, value: any) => void;


type TemplateFormContextProps = {
	schema: FormFieldDefinition[],
	setSchema: (value: FormFieldDefinition[]) => void;
	formData: TemplateForm;
	setFormData: (value: TemplateForm) => void,
	handleInputChange: HandleInputChangeType,
	handleInputBlur: HandleInputBlurType,
	errors: Errors;
	setErrors: (value: Errors) => void;
	validateAllTemplateFields: () => Errors;

	resetInitialState: () => void;
	isReadOnly?: boolean;
}

const TemplateFormContext = createContext<TemplateFormContextProps | undefined>(undefined);

export function useTemplateFormContext() {
	const context = useContext(TemplateFormContext);
	if (!context) throw new Error(`${useTemplateFormContext.name} must be used within a ${TemplateFormProvider.name}`);
	return context;
}

export function convertStringToTemplateForm(formData: TemplateForm, formSchema: FormFieldDefinition[]): TemplateForm {
	const outputForm: TemplateForm = {};

	try {
		formSchema.forEach(field => {
			switch (field['type']) {
				case 'text':
				case 'number':
				case 'select':
					outputForm[field.id] = formData[field.id]
					break;
				case 'multi-select-quantity':
					const typedValue = formData[field.id] as IMultiSelectQuantityValueType | null;
					outputForm[field.id] = typedValue?.values ? new MultiSelectQuantityValueType(typedValue.values) : null;
					break;
				default:
					break;
			}
		});
	} catch (err) {
		console.error(`Error parsing submission: ${err}`);
		throw err;
	}

	return outputForm;
}

interface TemplateFormProviderProps {
	children: ReactNode;
	initSchema?: FormFieldDefinition[];
	// So we can specify inital empty state for new forms on create or pass in initial form data for existing forms on edit
	initialFormData: TemplateForm;
	isReadonly?: boolean;
}


export function TemplateFormProvider(props: TemplateFormProviderProps) {
	const {
		children,
		initSchema,
		initialFormData,
		isReadonly = false,
	} = props;

	const [schema, setSchema] = useState<FormFieldDefinition[]>(initSchema ?? []);
	const [formData, setFormData] = useState<TemplateForm>(initialFormData);
	const [errors, setErrors] = useState<Errors>({});

	function resetInitialState() {
		setSchema(initSchema ?? []);
		setFormData(initialFormData);
		setErrors({});
	}

	const handleInputChange = (field: FormFieldDefinition, value: any) => {
		setFormData(prev => ({
			...prev,
			[field.id]: value
		}));
	};
	const handleInputBlur = (field: FormFieldDefinition, value: any) => {
		testFieldError(field, value)
	}

	function testFieldError(field: FormFieldDefinition, value: any) {
		const fieldValidationError = validateField(field, value)
		setErrors(prev => {
			const { [field.id]: _, ...rest } = prev;
			return fieldValidationError ? { ...rest, [field.id]: fieldValidationError } : rest;
		})
	}

	const validateAllTemplateFields = (): Errors => {
		const newErrors: Errors = {}
		schema.forEach(fieldDefinition => {
			const fieldValidationError = validateField(fieldDefinition, formData[fieldDefinition.id]);
			if (fieldValidationError) {
				newErrors[fieldDefinition.id] = fieldValidationError;
			}
		})

		// Return the new set of errors for us to perform validation against without rerender loops
		return newErrors;
	}

	const validateField = (field: FormFieldDefinition, value: TemplateFormValue): string | null => {
		const validation = field.validation;
		if (!validation) return null;

		if ('required' in validation && validation.required) {
			const isEmpty = value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
			if (isEmpty) return 'This field is required.';
		}

		switch (validation.type) {
			case 'text':
				if (validation.email && typeof value === 'string') {
					if (!isEmail(value)) return 'Invalid email format.';
				}
				break;
			case 'number':
				if (typeof value === 'number') {
					if (validation.min !== undefined && value < validation.min) return `Minimum value is ${validation.min}.`;
					if (validation.max !== undefined && value > validation.max) return `Maximum value is ${validation.max}.`;
				}
				break;
			case 'multi-select-quantity':
				if (value instanceof MultiSelectQuantityValueType) {
					if (validation.maxTotalQuantity != null && value.getTotalQuantity() > validation.maxTotalQuantity) return `Maximum quantity allowed is ${validation.maxTotalQuantity}`;
				}
				break;
		}

		return null;
	};

	const value: TemplateFormContextProps = {
		schema: schema,
		setSchema: setSchema,
		setFormData: setFormData,
		formData: formData,
		errors: errors,
		setErrors: setErrors,
		resetInitialState,
		handleInputChange: handleInputChange,
		handleInputBlur: handleInputBlur,
		validateAllTemplateFields: validateAllTemplateFields,
		isReadOnly: isReadonly,
	}

	return (
		<TemplateFormContext.Provider value={value}>
			{children}
		</TemplateFormContext.Provider>
	);
}